import React from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiUsers, FiAward, FiClock, FiHeart } = FiIcons;

const About = () => {
  const stats = [
    { icon: FiUsers, number: '500+', label: 'Happy Clients' },
    { icon: FiAward, number: '10+', label: 'Years Experience' },
    { icon: FiClock, number: '24/7', label: 'Support' },
    { icon: FiHeart, number: '100%', label: 'Satisfaction' }
  ];

  const team = [
    {
      name: 'Sarah Martinez',
      position: 'Master Stylist',
      image: 'https://images.unsplash.com/photo-1594824949480-9ed69b3b7b5e?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80'
    },
    {
      name: 'Emily Johnson',
      position: 'Color Specialist',
      image: 'https://images.unsplash.com/photo-1607990281513-2c110a25bd8c?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80'
    },
    {
      name: 'Jessica Chen',
      position: 'Nail Artist',
      image: 'https://images.unsplash.com/photo-1582750433449-648ed127bb54?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80'
    }
  ];

  return (
    <>
      <Helmet>
        <title>About Luxe Salon - Our Story & Expert Beauty Team | 10+ Years Experience</title>
        <meta name="description" content="Learn about Luxe Salon's story, our expert team of stylists, and 10+ years of providing premium beauty services. Meet our master stylists and color specialists." />
        <meta name="keywords" content="about luxe salon, beauty team, expert stylists, salon history, professional stylists, master stylists, color specialists" />
        <meta property="og:title" content="About Luxe Salon - Our Story & Expert Beauty Team" />
        <meta property="og:description" content="Discover Luxe Salon's journey and meet our team of expert stylists with 10+ years of experience in premium beauty services." />
        <meta property="og:type" content="website" />
      </Helmet>

      <div className="pt-20 bg-gradient-to-b from-black via-gray-900 to-black min-h-screen">
        {/* Hero Section */}
        <section className="py-20 relative overflow-hidden">
          <div className="max-w-7xl mx-auto px-4">
            <motion.div 
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h1 className="text-5xl md:text-6xl font-bold mb-6">
                <span className="bg-gradient-to-r from-red-400 via-yellow-400 to-red-400 bg-clip-text text-transparent">
                  About Us
                </span>
              </h1>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                Discover the story behind Luxe Salon and our passion for creating beautiful transformations
              </p>
            </motion.div>
          </div>
        </section>

        {/* Story Section */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
              >
                <h2 className="text-4xl font-bold mb-6 text-white">Our Story</h2>
                <p className="text-gray-300 mb-6 leading-relaxed">
                  Founded in 2014, Luxe Salon has been at the forefront of beauty innovation, 
                  combining traditional techniques with modern trends to create stunning looks 
                  that enhance our clients' natural beauty.
                </p>
                <p className="text-gray-300 mb-6 leading-relaxed">
                  Our team of expert stylists brings years of experience and passion to every 
                  appointment, ensuring that each client leaves feeling confident and beautiful.
                </p>
                <p className="text-gray-300 leading-relaxed">
                  We believe that beauty is not just about appearance, but about feeling 
                  confident and empowered. That's why we're committed to providing exceptional 
                  service in a luxurious and welcoming environment.
                </p>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                className="relative"
              >
                <img 
                  src="https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
                  alt="Luxe Salon Interior - Premium Beauty Services"
                  className="rounded-lg shadow-2xl"
                />
                <div className="absolute inset-0 bg-gradient-to-tr from-red-500/20 to-yellow-500/20 rounded-lg"></div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-20 bg-gradient-to-r from-red-900/20 to-yellow-900/20">
          <div className="max-w-7xl mx-auto px-4">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
              {stats.map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: index * 0.1 }}
                  className="text-center"
                >
                  <div className="bg-gradient-to-r from-red-500 to-yellow-500 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <SafeIcon icon={stat.icon} className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-3xl font-bold text-white mb-2">{stat.number}</h3>
                  <p className="text-gray-300">{stat.label}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Team Section */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4">
            <motion.div 
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl md:text-5xl font-bold mb-4">
                <span className="bg-gradient-to-r from-red-400 via-yellow-400 to-red-400 bg-clip-text text-transparent">
                  Meet Our Team
                </span>
              </h2>
              <p className="text-gray-300 text-lg max-w-2xl mx-auto">
                Our talented stylists are passionate about making you look and feel your best
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {team.map((member, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: index * 0.2 }}
                  whileHover={{ scale: 1.05 }}
                  className="text-center group"
                >
                  <div className="relative mb-6 overflow-hidden rounded-lg">
                    <img 
                      src={member.image}
                      alt={`${member.name} - ${member.position} at Luxe Salon`}
                      className="w-full h-80 object-cover transition-transform duration-300 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-2">{member.name}</h3>
                  <p className="text-yellow-400">{member.position}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default About;